import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  CssBaseline,
  Alert,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  ContentBox,
  StyledButton,
  TitleText,
  PageContainer,
} from "./Default.js";
import axios from "axios";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RelatedTools from "../Components/RelatedTools.js";
import { message } from "../Constants/message.js";
import CreditsBanner from "../Components/CreditsNudge";
import UrlDecoderContent from "../Contents/UrlDecoderContent.js";
import CommentSection from "../Components/CommentSection.js";

export default function UrlDecoderTool({ user, login, isLoggedIn }) {
  const [inputUrl, setInputUrl] = useState("");
  const [decodedUrl, setDecodedUrl] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedData = localStorage.getItem("urlDecode");
    if (savedData) {
      setInputUrl(savedData);
    }
  }, []);

  useEffect(() => {
    if (inputUrl) {
      localStorage.setItem("urlDecode", inputUrl);
    }
  }, [inputUrl]);

  const handleDecode = async () => {
    setError("");
    setSuccess("");
  
    try {
      const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
      const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";
      const guestUsedKey = `${toolKey}_guestUsed`;
  
      // One-time access check
      const guestUsed = localStorage.getItem(guestUsedKey);
  
      // If not logged in and guest access already used, redirect to login
      if (!isLoggedIn && guestUsed) {
        setError(message.loginPrompt);
        setTimeout(() => {
          login();
        }, 1500);
        return;
      }
  
      setLoading(true);
  
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        if (credits.remaining < 1) {
          setError("Insufficient credits to decode URLs. Please upgrade your plan.");
          setLoading(false);
          return;
        }
      }
  
      const decodedCandidate = decodeURIComponent(inputUrl.replace(/\+/g, " "));
      if (inputUrl === decodedCandidate) {
        setError("This URL is already decoded.");
        setSuccess("");
        setLoading(false);
        return;
      }
  
      const result = decodedCandidate;
  
      // Update credits if logged in
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        const creditsResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
          { toolName: "url_decoder" }
        );
        setCredits({
          remaining: creditsResponse.data.remaining_credits || 0,
          availed: creditsResponse.data.availed_credits || 0,
          total: creditsResponse.data.total_credits || 0,
        });
      } else if (isLoggedIn && user.account_type.toLowerCase() === "paid") {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
          { toolName: "url_decoder" }
        );
      }
  
      // If guest used the tool, mark it as used
      if (!isLoggedIn && !guestUsed) {
        localStorage.setItem(guestUsedKey, "true");
      }
  
      setDecodedUrl(result);
      setSuccess("URL decoded successfully!");
      setError("");
    } catch (e) {
      setError("Failed to decode URL.");
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };
  

  const copyToClipboard = () => {
    navigator.clipboard.writeText(decodedUrl);
    setSuccess("Decoded URL copied to clipboard!");
  };

  const relatedTools = [
    { name: "URL Encoder Online", path: "/url-encoder-online", emoji: "📎" },
    { name: "Convert JSON to CSV", path: "/json-to-csv", emoji: "📄" },
    { name: "Convert CSV to JSON", path: "/csv-to-json", emoji: "🔁" },
  ];

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            URL Decoder 🔓
          </TitleText>

          <Typography
            variant="subtitle1"
            align="center"
            sx={{ mt: 1, color: "text.secondary" }}
          >
            Decode your encoded URLs back to their original readable format.
          </Typography>

          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Enter Encoded URL"
              multiline
              minRows={3}
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="https%3A%2F%2Fexample.com%2Fsearch%3Fquery%3Dhello%2520world%26lang%3Den"
              variant="outlined"
              fullWidth
            />

            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleDecode}
            >
              Decode URL
            </StyledButton>
            {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>⚠️ Free Plan Limitations:</b> For Free plan users, 1 credit
                will be deducted for each conversion. <b>All Paid plan users</b>{" "}
                enjoy this tool for free 🎁 <b>without any credit deduction</b>.
              </Typography>
            ) : (
              isLoggedIn && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  mt={2}
                >
                  <b>
                    🎁 Good news! For all Paid plan users, email generation and
                    downloads are completely complimentary — no credits will be
                    deducted.
                  </b>
                </Typography>
              )
            )}

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {success && (
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {decodedUrl && (
              <>
                <Box display="flex" gap={1} justifyContent="center">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={copyToClipboard}
                    aria-label="Copy Decoded URL"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    minHeight: 100,
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  }}
                >
                  {decodedUrl}
                </Box>
              </>
            )}
          </Box>
        </ContentBox>
        <CommentSection
          toolId="url-decoder-online"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <RelatedTools relatedTools={relatedTools} />
        <UrlDecoderContent />
      </PageContainer>
    </>
  );
}
